import React, { useState, useEffect, useCallback } from 'react';
import { T, font, L } from './utils/theme';
import * as api from './utils/api';
import { loadDataCache, saveDataCache, flushQueue, getQueue } from './utils/offlineSync';
import { hasUnsaved } from './utils/unsaved';
import { getToken, clearToken, getShopName, setShop, getShopCurrency } from './utils/session';
import { currencySymbol } from './utils/money';
import { LangToggle, NavBubble, Btn, Inp, Card, IcBack, IcLock, Modal } from './components/UI';
import { McConnect, McPanel, connectRequest } from './components/AdminMissionControl';
import AdminDashboard from './components/AdminDashboard';
import AdminProducts from './components/AdminProducts';
import AdminReorder from './components/AdminReorder';
import AdminQuoting from './components/AdminQuoting';
import AdminOrdering from './components/AdminOrdering';
import AdminReports from './components/AdminReports';
import AdminSuppliers from './components/AdminSuppliers';
import AdminShipments from './components/AdminShipments';
import AdminPastOrders from './components/AdminPastOrders';
import SupplierDashboard from './components/SupplierDashboard';
import SupplierQuotes from './components/SupplierQuotes';
import SupplierAssigned from './components/SupplierAssigned';
import SupplierShipments from './components/SupplierShipments';
import SupplierPastOrders from './components/SupplierPastOrders';

// Determine mode from URL path
function getRouteMode() {
  const path = window.location.pathname.toLowerCase();
  if (path.startsWith('/supplier')) return 'supplier';
  if (path.startsWith('/admin')) return 'admin';
  return 'landing'; // root shows a choice
}

export default function App() {
  const [lang, setLang] = useState(() => localStorage.getItem('ts_lang') || 'en');
  const [routeMode] = useState(getRouteMode);
  const [view, setView] = useState(() => localStorage.getItem('ts_admin_tab') || 'dashboard');
  const [sView, setSView] = useState(() => localStorage.getItem('ts_supplier_tab') || 'dashboard');
  const [products, setProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]); // includes deleted
  const [suppliers, setSuppliers] = useState([]);
  const [activity, setActivity] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [shipmentComments, setShipmentComments] = useState([]);
  const [urgentReorderCount, setUrgentReorderCount] = useState(0);

  // Offline state
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);
  const [queueLength, setQueueLength] = useState(() => getQueue().length);
  // A release is only any use to somebody who reloads. Suppliers leave this tab open for days, so
  // the page watches the build the server reports and offers a reload when it changes.
  const [newBuild, setNewBuild] = useState(false);
  const myBuild = React.useRef(null);

  // Admin login - restore from localStorage (only with the pass that goes with it)
  // Each portal only sees its own session. Both can be signed in at once now, so without this the
  // admin screens' own fetches would fire while you are on the supplier portal and be refused.
  const [adminLoggedIn, setAdminLoggedIn] = useState(() => getRouteMode() === 'admin' && localStorage.getItem('ts_admin_logged_in') === 'true' && !!getToken('admin'));
  const [adminUser, setAdminUser] = useState('');
  const [adminPass, setAdminPass] = useState('');
  const [adminErr, setAdminErr] = useState('');
  const [showChangePw, setShowChangePw] = useState(false);
  // "Connect to Mission Control" (card connuv2g86pr stage 2). Read once from the address bar: Mission
  // Control sends the person here with a workspace id and a return address, they sign in as usual, and
  // the Connect page then replaces the admin panel until they confirm or cancel.
  // The Mission Control button shows only while this shop is linked (owner's decision, 2026-10-07): a shop
  // that linked knows Mission Control exists, and one that never did must not learn of it here. Linking
  // starts in Mission Control; the button opens the panel where a linked shop can see and end the link.
  const [mcRequest, setMcRequest] = useState(connectRequest);
  const [mcLinked, setMcLinked] = useState(false);
  const [showMcPanel, setShowMcPanel] = useState(false);
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [pwMsg, setPwMsg] = useState('');

  // Supplier login - restore from localStorage (only with the pass that goes with it)
  const [loggedIn, setLoggedIn] = useState(() => {
    if (getRouteMode() !== 'supplier') return null;
    try { const s = localStorage.getItem('ts_supplier'); return s && getToken('supplier') ? JSON.parse(s) : null; } catch { return null; }
  });
  const [loginName, setLoginName] = useState('');
  const [loginPass, setLoginPass] = useState('');
  const [loginErr, setLoginErr] = useState('');
  // Shown on the login screen when the backend refused the pass
  const [sessionMsg, setSessionMsg] = useState('');

  // Persist lang and active tabs
  useEffect(() => { localStorage.setItem('ts_lang', lang); }, [lang]);
  useEffect(() => { localStorage.setItem('ts_admin_tab', view); }, [view]);
  useEffect(() => { localStorage.setItem('ts_supplier_tab', sView); }, [sView]);

  const Lk = useCallback((k) => L(lang, k), [lang]);

  const reload = useCallback(async () => {
    try {
      const [prods, allProds, sups, acts, ships, convos, sComments] = await Promise.all([
        api.getProducts(), api.getAllProducts(), api.getSuppliers(), api.getActivity(), api.getShipments(), api.getConversations(), api.getAllShipmentComments(),
      ]);
      setProducts(prods); setAllProducts(allProds); setSuppliers(sups); setActivity(acts); setShipments(ships); setConversations(convos); setShipmentComments(sComments);
      saveDataCache({ products: prods, allProducts: allProds, suppliers: sups, activity: acts, shipments: ships, conversations: convos, shipmentComments: sComments });
      setQueueLength(getQueue().length);
    } catch (e) {
      const cached = await loadDataCache();
      if (cached) {
        setProducts(cached.products || []); setAllProducts(cached.allProducts || []);
        setSuppliers(cached.suppliers || []); setActivity(cached.activity || []);
        setShipments(cached.shipments || []); setConversations(cached.conversations || []);
        setShipmentComments(cached.shipmentComments || []);
      }
      setQueueLength(getQueue().length);
      console.error('Failed to load (offline, serving cache):', e);
    }
  }, []);

  const refreshUrgentCount = useCallback(() => {
    if (!adminLoggedIn) return; // the reorder forecast is an admin-only report
    api.getReorderPrediction().then(preds => {
      setUrgentReorderCount(preds.filter(p => ['overdue', 'due_soon'].includes(p.urgency)).length);
    }).catch(() => {});
  }, [adminLoggedIn]);

  // Watch for a new build, signed in or not. Nothing reloads by itself: a supplier halfway through
  // typing prices should not lose them, so it is offered and they choose when.
  useEffect(() => {
    let stopped = false;
    const check = async () => {
      try {
        const { build } = await api.getVersion();
        if (stopped || !build) return;
        if (myBuild.current === null) myBuild.current = build;
        else if (build !== myBuild.current) setNewBuild(true);
      } catch (e) { /* offline, or the server is restarting mid-deploy; try again next time */ }
    };
    check();
    const t = setInterval(check, 60000);
    return () => { stopped = true; clearInterval(t); };
  }, []);

  // Every /api call needs a pass, so nothing is fetched before sign-in
  // Asked when an admin signs in and again when the panel closes (it may just have been unlinked). Any
  // failure reads as "not linked": the button is hidden rather than shown by mistake.
  useEffect(() => {
    if (!adminLoggedIn || showMcPanel) return;
    let live = true;
    api.getMcLink().then(l => { if (live) setMcLinked(l?.linked === true); }).catch(() => { if (live) setMcLinked(false); });
    return () => { live = false; };
  }, [adminLoggedIn, showMcPanel, mcRequest]);

  const signedIn = adminLoggedIn || !!loggedIn;

  // Which shop this session is in, shown in the corner so two shops are never confused for each other.
  // It is stored at sign-in; a session from before that is filled in once from the server.
  const [shopName, setShopName] = useState(() => getShopName());
  const [shopCurrency, setShopCurrency] = useState(() => getShopCurrency());
  useEffect(() => {
    if (!signedIn || shopName) return;
    let cancelled = false;
    api.getMyShop()
      .then(s => { if (!cancelled && s?.name) { setShop(s.id, undefined, s.name, s.currency); setShopName(s.name); if (s.currency) setShopCurrency(s.currency); } })
      .catch(() => { /* the corner simply stays blank */ });
    return () => { cancelled = true; };
  }, [signedIn, shopName]);

  // The backend refused the pass: back to the login screen with a plain message
  useEffect(() => {
    const onExpired = () => {
      setAdminLoggedIn(false);
      setLoggedIn(null);
      setSessionMsg(lang === 'zh' ? '您的登录已过期，请重新登录。' : 'Your sign-in has expired. Please log in again.');
    };
    window.addEventListener('ts-session-expired', onExpired);
    return () => window.removeEventListener('ts-session-expired', onExpired);
  }, [lang]);

  // Initial load + smart auto-poll (15 seconds, pauses during user activity)
  useEffect(() => { if (signedIn) reload(); }, [reload, signedIn]);
  useEffect(() => { refreshUrgentCount(); }, [refreshUrgentCount]);
  useEffect(() => {
    if (!signedIn) return;
    let lastActivity = Date.now();
    const onActivity = () => { lastActivity = Date.now(); };
    window.addEventListener('click', onActivity);
    window.addEventListener('keydown', onActivity);

    const interval = setInterval(() => {
      // Only poll if user hasn't clicked/typed in the last 3 seconds
      if (Date.now() - lastActivity > 3000) {
        reload();
        refreshUrgentCount();
      }
    }, 15000);

    return () => {
      clearInterval(interval);
      window.removeEventListener('click', onActivity);
      window.removeEventListener('keydown', onActivity);
    };
  }, [reload, refreshUrgentCount, signedIn]);

  // Online/offline sync
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      if (!signedIn) return;
      setIsSyncing(true);
      flushQueue(() => {
        reload();
        setIsSyncing(false);
        setQueueLength(0);
      });
      setTimeout(() => setIsSyncing(false), 4000);
    };
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [reload, signedIn]);

  const activeSup = suppliers.filter(s => s.active);
  // The rows the Quoting screen lists: a suggested version still waiting on us is reviewed inside its
  // item there, not as a row of its own, so it is not counted here either.
  const quotingP = products.filter(p => p.status === 'quoting' && !(p.variant_state === 'provisional' && p.restrict_supplier));
  const assignedP = products.filter(p => p.status === 'supplier_assigned');
  const orderingP = products.filter(p => p.status === 'ordering');

  // Admin login handler
  const doAdminLogin = async () => {
    try {
      await api.adminLogin(adminUser, adminPass);
      setAdminLoggedIn(true);
      localStorage.setItem('ts_admin_logged_in', 'true');
      setAdminErr(''); setSessionMsg('');
    } catch (e) {
      setAdminErr(lang === 'zh' ? '用户名或密码无效。' : 'Invalid username or password.');
    }
  };

  // Change admin password
  const doChangePw = async () => {
    if (!currentPw) { setPwMsg(lang === 'zh' ? '请输入当前密码' : 'Enter your current password'); return; }
    if (!newPw || newPw.length < 6) { setPwMsg(lang === 'zh' ? '密码至少6个字符' : 'Password must be at least 6 characters'); return; }
    if (newPw !== confirmPw) { setPwMsg(lang === 'zh' ? '密码不匹配' : 'Passwords do not match'); return; }
    try {
      await api.changeAdminPassword(currentPw, newPw);
      setPwMsg(lang === 'zh' ? '✓ 密码已更新' : '✓ Password updated');
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
      setTimeout(() => { setShowChangePw(false); setPwMsg(''); }, 1500);
    } catch (e) {
      const wrongCurrent = /current password/i.test(e.message || '');
      if (wrongCurrent) setPwMsg(lang === 'zh' ? '当前密码不正确' : 'Current password is incorrect');
      else setPwMsg(e.message || (lang === 'zh' ? '更新密码时出错' : 'Error updating password'));
    }
  };

  // Supplier login handler
  const doSupLogin = async () => {
    try {
      const res = await api.supplierLogin(loginName, loginPass);
      setLoggedIn(res.supplier);
      localStorage.setItem('ts_supplier', JSON.stringify(res.supplier));
      setLoginErr(''); setSessionMsg('');
    } catch (e) {
      setLoginErr(lang === 'zh' ? '名称或密码无效。' : 'Invalid supplier name or password.');
    }
  };

  const supCanSee = p => loggedIn && (!p.restrict_supplier || p.restrict_supplier === loggedIn.id);
  const supToQuote = loggedIn ? products.filter(p => p.status === 'quoting' && supCanSee(p) && !p.quotes?.[loggedIn.id]) : [];
  const supAssigned = loggedIn ? products.filter(p => p.status === 'supplier_assigned' && p.assigned_supplier === loggedIn.id) : [];

  // ═══ LANDING PAGE (root URL) ═══
  if (routeMode === 'landing') {
    return (
      <div style={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center', background: T.bg, fontFamily: font }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 28, fontWeight: 900, color: T.text, marginBottom: 8 }}>Tan Star Procurement</div>
          <div style={{ fontSize: 14, color: T.dim, marginBottom: 30 }}>{lang === 'zh' ? '选择入口' : 'Select your portal'}</div>
          <div style={{ display: 'flex', gap: 16 }}>
            <Card style={{ padding: 30, cursor: 'pointer', minWidth: 180, textAlign: 'center' }} onClick={() => window.location.href = '/admin'}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>🔧</div>
              <div style={{ fontWeight: 800, color: T.text, fontSize: 16 }}>{Lk('adminPanel')}</div>
              <div style={{ fontSize: 12, color: T.dim, marginTop: 4 }}>{lang === 'zh' ? '管理产品和订单' : 'Manage products & orders'}</div>
            </Card>
            <Card style={{ padding: 30, cursor: 'pointer', minWidth: 180, textAlign: 'center' }} onClick={() => window.location.href = '/supplier'}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>📦</div>
              <div style={{ fontWeight: 800, color: T.text, fontSize: 16 }}>{Lk('supplierPortal')}</div>
              <div style={{ fontSize: 12, color: T.dim, marginTop: 4 }}>{lang === 'zh' ? '报价和发货' : 'Quote & ship products'}</div>
            </Card>
          </div>
          <div style={{ marginTop: 20 }}><LangToggle lang={lang} setLang={setLang} /></div>
        </div>
      </div>
    );
  }

  // ═══ ADMIN LOGIN ═══
  if (routeMode === 'admin' && !adminLoggedIn) {
    return (
      <div style={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center', background: T.bg, fontFamily: font, padding: '0 16px' }}>
        <div style={{ width: '100%', maxWidth: 370 }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}><LangToggle lang={lang} setLang={setLang} /></div>
          <Card style={{ padding: 28 }}>
            <div style={{ textAlign: 'center', marginBottom: 22 }}>
              <div style={{ fontSize: 20, fontWeight: 900, color: T.text }}>{Lk('adminPanel')}</div>
              <div style={{ fontSize: 12, color: T.dim, marginTop: 3 }}>{lang === 'zh' ? '管理员登录' : 'Admin Login'}</div>
              {/* Arriving from Mission Control's Connect button looks like being asked to sign in for no
                  reason, so the screen says what it is for. The password is this app's, as always:
                  Mission Control never sees it. */}
              {mcRequest && (
                <div style={{ fontSize: 12, color: T.accent, fontWeight: 700, marginTop: 10, lineHeight: 1.5 }}>
                  {lang === 'zh' ? '请以店铺管理员身份登录，以确认连接到 Mission Control。'
                                  : 'Sign in as a shop admin to confirm linking this shop to Mission Control.'}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <Inp label={lang === 'zh' ? '用户名' : 'Username'} name="username" autoComplete="username" value={adminUser} onChange={e => setAdminUser(e.target.value)} placeholder="admin" />
              <Inp label={Lk('password')} type="password" name="password" autoComplete="current-password" value={adminPass} onChange={e => setAdminPass(e.target.value)} placeholder={Lk('password')}
                onKeyDown={e => { if (e.key === 'Enter') doAdminLogin(); }} />
              {sessionMsg && !adminErr && <div style={{ fontSize: 12, color: T.warn, fontWeight: 700 }}>{sessionMsg}</div>}
              {adminErr && <div style={{ fontSize: 12, color: T.danger, fontWeight: 700 }}>{adminErr}</div>}
              <Btn onClick={doAdminLogin} style={{ justifyContent: 'center', marginTop: 2 }}>{Lk('logIn')}</Btn>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // The two tabs people click most often. Reloading there is a blunt but reliable way to pick up a
  // release and a fresh read of the data without anyone being told to refresh - over a working day
  // they will land on both. Prices typed but not yet saved are not worth losing to it, so when there
  // are any the data is refreshed in place instead and the release banner does the asking.
  const openTab = (setter, key, reloadOn, tabKey) => {
    setter(key);
    if (key !== reloadOn) return;
    if (hasUnsaved()) { reload(); refreshUrgentCount(); return; }
    // The tab is remembered in localStorage by an effect, which never gets to run before a reload,
    // so it is written here first. Without it the reload lands back on whichever tab was stored,
    // and this one can never be opened at all.
    try { localStorage.setItem(tabKey, key); } catch (e) { /* private window */ }
    window.location.reload();
  };

  // ═══ OFFLINE / NEW BUILD BANNER (shared) ═══
  const OfflineBanner = newBuild ? (
    <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 9999, background: T.accent, padding: '10px 18px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, fontSize: 13, fontWeight: 700, color: '#fff', fontFamily: font }}>
      <span>{lang === 'zh' ? '系统已更新' : 'The portal has been updated'}</span>
      <button onClick={() => window.location.reload()} style={{ fontFamily: font, fontSize: 13, fontWeight: 800, padding: '6px 16px', borderRadius: 7, border: 'none', background: '#fff', color: T.accent, cursor: 'pointer' }}>
        {lang === 'zh' ? '点击刷新' : 'Reload now'}
      </button>
    </div>
  ) : !isOnline ? (
    <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 9999, background: T.warnBg, borderTop: `1px solid ${T.warn}`, padding: '8px 18px', display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 700, color: T.warn, fontFamily: font }}>
      <span>⚠</span>
      <span>{lang === 'zh' ? '当前离线' : 'You are offline'}{queueLength > 0 ? (lang === 'zh' ? ` — ${queueLength} 项变更待同步` : ` — ${queueLength} change${queueLength !== 1 ? 's' : ''} queued`) : ''}</span>
    </div>
  ) : isSyncing ? (
    <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 9999, background: T.successBg, borderTop: `1px solid ${T.success}`, padding: '8px 18px', display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 700, color: T.success, fontFamily: font }}>
      <span>↑</span>
      <span>{lang === 'zh' ? '已重新连接，正在同步更改...' : 'Back online — syncing changes...'}</span>
    </div>
  ) : null;

  // ═══ CONNECT TO MISSION CONTROL ═══
  // Before the admin panel, because it is the whole reason this person was sent here. requireAdmin on the
  // routes behind it is what actually keeps a supplier out; this is only which screen is shown.
  if (routeMode === 'admin' && adminLoggedIn && mcRequest) {
    return <McConnect lang={lang} request={mcRequest} onDone={() => setMcRequest(null)} />;
  }

  // ═══ ADMIN PANEL ═══
  if (routeMode === 'admin') {
    const adminNav = [
      { key: 'dashboard', label: Lk('dashboard'), ct: 0 },
      { key: 'products', label: Lk('products'), ct: 0 },
      { key: 'reorder', label: lang === 'zh' ? '补货预测' : 'Reorder', ct: urgentReorderCount },
      { key: 'quoting', label: Lk('quoting'), ct: quotingP.length },
      { key: 'ordering', label: Lk('ordering'), ct: assignedP.length + orderingP.length },
      { key: 'shipments', label: Lk('shipments'), ct: shipments.filter(s => s.status === 'shipped').length },
      { key: 'pastOrders', label: Lk('pastOrders'), ct: 0 },
      { key: 'reports', label: lang === 'zh' ? '报表' : 'Reports', ct: 0 },
      { key: 'suppliers', label: Lk('suppliers'), ct: 0 },
    ];

    const props = { lang, products, allProducts, suppliers: activeSup, allSuppliers: suppliers, activity, shipments, conversations, shipmentComments, reload, Lk };

    const renderAdmin = () => {
      switch (view) {
        case 'dashboard': return <AdminDashboard {...props} />;
        case 'products': return <AdminProducts {...props} />;
        case 'reorder': return <AdminReorder {...props} onUrgentCountChange={refreshUrgentCount} />;
        case 'quoting': return <AdminQuoting {...props} />;
        case 'ordering': return <AdminOrdering {...props} />;
        case 'shipments': return <AdminShipments {...props} />;
        case 'pastOrders': return <AdminPastOrders {...props} />;
        case 'reports': return <AdminReports {...props} />;
        case 'suppliers': return <AdminSuppliers {...props} />;
        default: return null;
      }
    };

    return (
      <>
        <style>{`
          @media (max-width: 768px) {
            .admin-sidebar { display: none !important; }
            .admin-topnav { display: flex !important; }
            .admin-content { padding: 8px 12px 20px !important; }
            .admin-content-lang { display: none !important; }
            .admin-wrapper { flex-direction: column !important; }
          }
          @media (min-width: 769px) {
            .admin-topnav { display: none !important; }
          }
        `}</style>
        <div className="admin-wrapper" style={{ display: 'flex', height: '100vh', fontFamily: font, background: T.bg, overflow: 'hidden' }}>
          {/* Desktop sidebar */}
          <div className="admin-sidebar" style={{ width: 210, background: T.surface, borderRight: `1px solid ${T.border}`, display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
            <div style={{ padding: '22px 18px 18px', borderBottom: `1px solid ${T.border}` }}>
              <div style={{ fontSize: 17, fontWeight: 900, color: T.text }}>{Lk('procure')}</div>
              <div style={{ fontSize: 10, color: T.dim, fontWeight: 700, marginTop: 2, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{Lk('adminPanel')}</div>
              {shopName && <div style={{ fontSize: 12, color: T.accent, fontWeight: 800, marginTop: 6 }}>{shopName}</div>}
            </div>
            <nav style={{ padding: '10px 8px', flex: 1, overflowY: 'auto' }}>
              {adminNav.map(n => (
                <div key={n.key} onClick={() => openTab(setView, n.key, 'products', 'ts_admin_tab')}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 12px', borderRadius: 7, cursor: 'pointer', marginBottom: 1, fontSize: 13, fontWeight: 600, background: view === n.key ? T.accent + '15' : 'transparent', color: view === n.key ? T.accent : T.muted }}>
                  <span>{n.label}</span>
                  {n.ct > 0 && <NavBubble count={n.ct} />}
                </div>
              ))}
            </nav>
            <div style={{ padding: '10px 8px', borderTop: `1px solid ${T.border}` }}>
              {/* What this shop's prices are in. It changes the symbol on every screen and converts
                  nothing: a price stored as 12.50 is still 12.50. */}
              <label style={{ padding: '7px 12px', display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 600, color: T.dim }}>
                <span>{lang === 'zh' ? '货币' : 'Currency'}</span>
                <select
                  value={shopCurrency}
                  onChange={async (e) => {
                    const next = e.target.value;
                    const was = shopCurrency;
                    setShopCurrency(next);
                    try {
                      const shop = await api.setShopCurrency(next);
                      setShop(shop.id, 'admin', shop.name, shop.currency);
                    } catch (err) {
                      setShopCurrency(was);
                      alert(lang === 'zh' ? '无法更改货币' : 'Could not change the currency');
                    }
                  }}
                  style={{ flex: 1, minWidth: 0, background: T.bg, color: T.text, border: `1px solid ${T.border}`, borderRadius: 6, padding: '3px 6px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                  {['CNY', 'USD', 'CAD', 'EUR', 'GBP', 'HKD', 'JPY', 'AUD', 'SGD', 'TWD'].map(c => (
                    <option key={c} value={c}>{c} {currencySymbol(c)}</option>
                  ))}
                </select>
              </label>
              {mcLinked && (
                <div onClick={() => setShowMcPanel(true)} style={{ padding: '7px 12px', borderRadius: 7, cursor: 'pointer', fontSize: 11, fontWeight: 600, color: T.dim }}>
                  Mission Control
                </div>
              )}
              <div onClick={() => setShowChangePw(true)} aria-label="Change Password" style={{ padding: '7px 12px', borderRadius: 7, cursor: 'pointer', fontSize: 11, fontWeight: 600, color: T.dim, display: 'flex', alignItems: 'center', gap: 6 }}>
                {IcLock} {lang === 'zh' ? '修改密码' : 'Change Password'}
              </div>
              <div onClick={() => { setAdminLoggedIn(false); setAdminUser(''); setAdminPass(''); localStorage.removeItem('ts_admin_logged_in'); clearToken('admin'); }} style={{ padding: '7px 12px', borderRadius: 7, cursor: 'pointer', fontSize: 11, fontWeight: 600, color: T.dim }}>
                {Lk('logOut')}
              </div>
            </div>
          </div>

          {/* Mobile top navigation */}
          <div className="admin-topnav" style={{ display: 'none', flexDirection: 'column', background: T.surface, borderBottom: `1px solid ${T.border}`, position: 'sticky', top: 0, zIndex: 30 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 14px', borderBottom: `1px solid ${T.border}` }}>
              <div>
                <span style={{ fontSize: 14, fontWeight: 900, color: T.text }}>{Lk('procure')}</span>
                <span style={{ fontSize: 9, color: T.dim, fontWeight: 700, marginLeft: 6, textTransform: 'uppercase' }}>{Lk('adminPanel')}</span>
                {shopName && <div style={{ fontSize: 11, color: T.accent, fontWeight: 800, marginTop: 1 }}>{shopName}</div>}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <LangToggle lang={lang} setLang={setLang} />
                {mcLinked && (
                  <span onClick={() => setShowMcPanel(true)} aria-label="Mission Control" title="Mission Control" style={{ cursor: 'pointer', color: T.dim, fontSize: 10, fontWeight: 800, letterSpacing: '0.04em', border: `1px solid ${T.border}`, borderRadius: 5, padding: '2px 5px' }}>MC</span>
                )}
                <span onClick={() => setShowChangePw(true)} aria-label="Change Password" title={lang === 'zh' ? '修改密码' : 'Change Password'} style={{ cursor: 'pointer', color: T.dim, fontSize: 12 }}>{IcLock}</span>
                <Btn variant="ghost" style={{ fontSize: 10, padding: '4px 8px' }} onClick={() => { setAdminLoggedIn(false); setAdminUser(''); setAdminPass(''); localStorage.removeItem('ts_admin_logged_in'); clearToken('admin'); }}>{Lk('logOut')}</Btn>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '3px', padding: '6px 8px', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              {adminNav.map(n => (
                <div key={n.key} onClick={() => openTab(setView, n.key, 'products', 'ts_admin_tab')}
                  style={{ padding: '7px 10px', borderRadius: 7, fontSize: 11, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
                    background: view === n.key ? T.accent : 'transparent',
                    color: view === n.key ? '#fff' : T.muted,
                    border: `1px solid ${view === n.key ? T.accent : T.border}`,
                  }}>
                  {n.label}
                  {n.ct > 0 && <span style={{ marginLeft: 3, padding: '1px 5px', borderRadius: 10, fontSize: 8, background: view === n.key ? 'rgba(255,255,255,0.25)' : T.accent + '30', color: view === n.key ? '#fff' : T.accent }}>{n.ct}</span>}
                </div>
              ))}
            </div>
          </div>

          <div style={{ flex: 1, overflowY: 'auto' }}>
            <div className="admin-content-lang" style={{ display: 'flex', justifyContent: 'flex-end', padding: '16px 32px 0' }}><LangToggle lang={lang} setLang={setLang} /></div>
            <div className="admin-content" style={{ padding: '8px 32px 28px' }}>{renderAdmin()}</div>
          </div>

        {/* Linked to Mission Control, with Unlink: only reachable while linked */}
        <Modal open={showMcPanel} onClose={() => setShowMcPanel(false)}>
          <McPanel lang={lang} onClose={() => setShowMcPanel(false)} />
        </Modal>

        {/* Change password modal */}
        <Modal open={showChangePw} onClose={() => { setShowChangePw(false); setPwMsg(''); setCurrentPw(''); setNewPw(''); setConfirmPw(''); }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: T.text, marginBottom: 16 }}>{lang === 'zh' ? '修改管理员密码' : 'Change Admin Password'}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <Inp label={lang === 'zh' ? '当前密码' : 'Current Password'} type="password" autoComplete="current-password" value={currentPw} onChange={e => setCurrentPw(e.target.value)} placeholder={lang === 'zh' ? '您现在的密码' : 'Your password right now'} />
              <Inp label={lang === 'zh' ? '新密码' : 'New Password'} type="password" autoComplete="new-password" value={newPw} onChange={e => setNewPw(e.target.value)} placeholder={lang === 'zh' ? '至少6个字符' : 'At least 6 characters'} />
              <Inp label={lang === 'zh' ? '确认密码' : 'Confirm Password'} type="password" autoComplete="new-password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} placeholder={lang === 'zh' ? '再次输入' : 'Type again'}
                onKeyDown={e => { if (e.key === 'Enter') doChangePw(); }} />
              {pwMsg && <div style={{ fontSize: 12, fontWeight: 700, color: pwMsg.startsWith('✓') ? T.success : T.danger }}>{pwMsg}</div>}
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <Btn variant="secondary" onClick={() => { setShowChangePw(false); setPwMsg(''); setCurrentPw(''); setNewPw(''); setConfirmPw(''); }} style={{ flex: 1 }}>{Lk('cancel')}</Btn>
              <Btn onClick={doChangePw} style={{ flex: 1 }}>{Lk('save')}</Btn>
            </div>
          </div>
        </Modal>
      </div>
      {OfflineBanner}
      </>
    );
  }

  // ═══ SUPPLIER LOGIN ═══
  if (routeMode === 'supplier' && !loggedIn) {
    return (
      <div style={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center', background: T.bg, fontFamily: font, padding: '0 16px' }}>
        <div style={{ width: '100%', maxWidth: 370 }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}><LangToggle lang={lang} setLang={setLang} /></div>
          <Card style={{ padding: 28 }}>
            <div style={{ textAlign: 'center', marginBottom: 22 }}>
              <div style={{ fontSize: 20, fontWeight: 900, color: T.text }}>{Lk('supplierPortal')}</div>
              <div style={{ fontSize: 12, color: T.dim, marginTop: 3 }}>{lang === 'zh' ? '使用您的凭据登录' : 'Log in with your credentials'}</div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <Inp label={Lk('supplierName')} name="supplier" autoComplete="username" value={loginName} onChange={e => setLoginName(e.target.value)} placeholder={lang === 'zh' ? '您的公司名称' : 'Your company name'} />
              <Inp label={Lk('password')} type="password" name="password" autoComplete="current-password" value={loginPass} onChange={e => setLoginPass(e.target.value)} placeholder={Lk('password')}
                onKeyDown={e => { if (e.key === 'Enter') doSupLogin(); }} />
              {sessionMsg && !loginErr && <div style={{ fontSize: 12, color: T.warn, fontWeight: 700 }}>{sessionMsg}</div>}
              {loginErr && <div style={{ fontSize: 12, color: T.danger, fontWeight: 700 }}>{loginErr}</div>}
              <Btn onClick={doSupLogin} style={{ justifyContent: 'center', marginTop: 2 }}>{Lk('logIn')}</Btn>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // ═══ SUPPLIER PORTAL ═══
  if (routeMode === 'supplier' && loggedIn) {
    const supNav = [
      { key: 'dashboard', label: Lk('dashboard'), ct: 0 },
      { key: 'quotes', label: Lk('quotesTab'), ct: supToQuote.length },
      { key: 'assigned', label: Lk('assignedOrders'), ct: supAssigned.length },
      { key: 'shipments', label: Lk('shipments'), ct: 0 },
      { key: 'pastOrders', label: Lk('pastOrders'), ct: 0 },
    ];

    const supProps = { lang, supplier: loggedIn, products, allProducts, suppliers: activeSup, allSuppliers: suppliers, shipments, conversations, shipmentComments, reload, Lk };

    const renderSupplier = () => {
      switch (sView) {
        case 'dashboard': return <SupplierDashboard {...supProps} />;
        case 'quotes': return <SupplierQuotes {...supProps} />;
        case 'assigned': return <SupplierAssigned {...supProps} />;
        case 'shipments': return <SupplierShipments {...supProps} />;
        case 'pastOrders': return <SupplierPastOrders {...supProps} />;
        default: return null;
      }
    };

    return (
      <>
        <style>{`
          @media (max-width: 768px) {
            .sup-sidebar { display: none !important; }
            .sup-topnav { display: flex !important; }
            .sup-content { padding: 8px 12px 20px !important; }
            .sup-wrapper { flex-direction: column !important; }
          }
          @media (min-width: 769px) {
            .sup-topnav { display: none !important; }
          }
        `}</style>
        <div className="sup-wrapper" style={{ display: 'flex', height: '100vh', fontFamily: font, background: T.bg, overflow: 'hidden' }}>
          {/* Desktop sidebar */}
          <div className="sup-sidebar" style={{ width: 210, background: T.surface, borderRight: `1px solid ${T.border}`, display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
            <div style={{ padding: '22px 18px 18px', borderBottom: `1px solid ${T.border}` }}>
              <div style={{ fontSize: 17, fontWeight: 900, color: T.text }}>{Lk('supplierPortal')}</div>
              <div style={{ fontSize: 10, color: T.accent, fontWeight: 700, marginTop: 2 }}>{loggedIn.name}</div>
              {shopName && <div style={{ fontSize: 11, color: T.dim, fontWeight: 700, marginTop: 4 }}>{shopName}</div>}
            </div>
            <nav style={{ padding: '10px 8px', flex: 1 }}>
              {supNav.map(n => (
                <div key={n.key} onClick={() => openTab(setSView, n.key, 'quotes', 'ts_supplier_tab')}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 12px', borderRadius: 7, cursor: 'pointer', marginBottom: 1, fontSize: 13, fontWeight: 600, background: sView === n.key ? T.accent + '15' : 'transparent', color: sView === n.key ? T.accent : T.muted }}>
                  <span>{n.label}</span>
                  {n.ct > 0 && <NavBubble count={n.ct} />}
                </div>
              ))}
            </nav>
            <div style={{ padding: '10px 8px', borderTop: `1px solid ${T.border}` }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 12px' }}>
                <LangToggle lang={lang} setLang={setLang} />
                <Btn variant="ghost" style={{ fontSize: 11 }} onClick={() => { setLoggedIn(null); setLoginName(''); setLoginPass(''); setSView('dashboard'); localStorage.removeItem('ts_supplier'); clearToken('supplier'); }}>{Lk('logOut')}</Btn>
              </div>
            </div>
          </div>

          {/* Mobile top navigation */}
          <div className="sup-topnav" style={{ display: 'none', flexDirection: 'column', background: T.surface, borderBottom: `1px solid ${T.border}`, position: 'sticky', top: 0, zIndex: 30 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderBottom: `1px solid ${T.border}` }}>
              <div>
                <span style={{ fontSize: 15, fontWeight: 900, color: T.text }}>{Lk('supplierPortal')}</span>
                <span style={{ fontSize: 10, color: T.accent, fontWeight: 700, marginLeft: 8 }}>{loggedIn.name}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <LangToggle lang={lang} setLang={setLang} />
                <Btn variant="ghost" style={{ fontSize: 10, padding: '4px 8px' }} onClick={() => { setLoggedIn(null); setLoginName(''); setLoginPass(''); setSView('dashboard'); localStorage.removeItem('ts_supplier'); clearToken('supplier'); }}>{Lk('logOut')}</Btn>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 4, padding: '8px 10px', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              {supNav.map(n => (
                <div key={n.key} onClick={() => openTab(setSView, n.key, 'quotes', 'ts_supplier_tab')}
                  style={{ padding: '8px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
                    background: sView === n.key ? T.accent : 'transparent',
                    color: sView === n.key ? '#fff' : T.muted,
                    border: `1px solid ${sView === n.key ? T.accent : T.border}`,
                  }}>
                  {n.label}
                  {n.ct > 0 && <span style={{ marginLeft: 4, padding: '1px 6px', borderRadius: 10, fontSize: 9, background: sView === n.key ? 'rgba(255,255,255,0.25)' : T.accent + '30', color: sView === n.key ? '#fff' : T.accent }}>{n.ct}</span>}
                </div>
              ))}
            </div>
          </div>

          <div className="sup-content" style={{ flex: 1, overflowY: 'auto', padding: '28px 32px' }}>
            {renderSupplier()}
          </div>
        </div>
      {OfflineBanner}
      </>
    );
  }

  return null;
}
